import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { Button } from '@/components/ui/Buttons';
import setupCsrfToken from '@/modules/SetupCSRFToken';
import type { GenerationRow, GenerationsMeta, GenerationsPayload, GenerationScore } from './types';

export const POLL_INTERVAL_MS = 30_000;
export const ROWS_PER_PAGE = 50;

const JUDGE_CHIP_CLASSES: Record<number, string> = { 1: 'fail', 2: 'low', 3: 'mid', 4: 'ok', 5: 'ok' };

const scoreLabel = (name: string) => name
  .replace(/^lr-/, '')
  .split('-')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const TAG_LABELS: Record<string, string> = {
  'lr-has-quotes': 'quotes',
  'lr-sensitive-topic': 'sensitive topic',
};

interface ScoreChipProps {
  chipClass: string;
  text: string;
  title?: string;
}

const ScoreChip = ({ chipClass, text, title }: ScoreChipProps) => (
  <span className={`chip ${chipClass}`} title={title}>{text}</span>
);

interface JudgeCellProps {
  score: GenerationScore | undefined;
}

const JudgeCell = ({ score }: JudgeCellProps) => {
  if (!score) return <td style={{ textAlign: 'center' }}><span className="chip none">—</span></td>;

  const value = Math.round(score.value);
  return (
    <td style={{ textAlign: 'center' }}>
      <ScoreChip
        chipClass={JUDGE_CHIP_CLASSES[value] || 'mid'}
        text={String(value)}
        title={score.comment || undefined}
      />
    </td>
  );
};

interface ChecksCellProps {
  checkNames: string[];
  scores: Record<string, GenerationScore>;
}

const ChecksCell = ({ checkNames, scores }: ChecksCellProps) => {
  const present = checkNames.filter(name => scores[name]);
  if (present.length === 0) return <td style={{ textAlign: 'center' }}><span className="chip none">—</span></td>;

  // Blocking violations never save, so any flagged check on a saved row is warning-tier: amber.
  const flagged = present.filter(name => Math.round(scores[name].value) === 0);
  if (flagged.length > 0) {
    const title = flagged.map(name => `${scoreLabel(name)}: ${scores[name].comment || 'flagged'}`).join('; ');
    return (
      <td style={{ textAlign: 'center' }}>
        <ScoreChip chipClass="low" text={`⚠ ${flagged.length}`} title={title} />
      </td>
    );
  }
  return (
    <td style={{ textAlign: 'center' }}>
      <ScoreChip chipClass="ok" text="✓" title={`All ${present.length} deterministic checks passed`} />
    </td>
  );
};

const CORRECTION_CHIP: Record<string, string> = {
  approved: 'ok',
  error: 'low',
  failed_check: 'low',
  pending: 'mid',
  rejected: 'fail',
  stale: 'off',
};

// failed_check/error drafts never reached review — label them the way the corrections page does.
const CORRECTION_LABELS: Record<string, string> = {
  error: 'draft error',
  failed_check: 'draft discarded',
};

interface CorrectionCellProps {
  canDraft: boolean;
  canRegenerate: boolean;
  correction: { id: number; status: string } | null;
  correctionsUrl: string;
  regenerated: boolean;
  requested: boolean;
  onDraft: () => void;
  onRegenerate: () => void;
}

// Latest correction proposal for this reading level, linking straight to that proposal in the
// corrections queue. Rows whose warnings are retention-shaped (correctable) offer a one-click
// draft request.
const CorrectionCell = ({
  canDraft, canRegenerate, correction, correctionsUrl, regenerated, requested, onDraft, onRegenerate,
}: CorrectionCellProps) => {
  if (correction) {
    return (
      <td style={{ textAlign: 'center' }}>
        <a
          href={`${correctionsUrl}#proposal-${correction.id}`}
          rel="noopener noreferrer"
          target="_blank"
          title="Open this proposal in the corrections queue"
        >
          <span
            className={`chip ${CORRECTION_CHIP[correction.status] || 'mid'}`}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {CORRECTION_LABELS[correction.status] || correction.status} ↗
          </span>
        </a>
      </td>
    );
  }
  if (requested) return <td style={{ textAlign: 'center' }}><span className="chip mid">requested…</span></td>;
  if (canDraft) {
    return (
      <td style={{ textAlign: 'center' }}>
        <Button outlineStyle small onClick={onDraft}>Draft fix</Button>
      </td>
    );
  }
  if (regenerated) return <td style={{ textAlign: 'center' }}><span className="chip mid">regenerating…</span></td>;
  if (canRegenerate) {
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          outlineStyle
          small
          title="Reading level is outside its band — request a fresh generation"
          onClick={onRegenerate}
        >
          Regenerate
        </Button>
      </td>
    );
  }
  return <td style={{ textAlign: 'center' }}><span className="chip none">—</span></td>;
};

const hasCorrectableWarning = (row: GenerationRow, correctableChecks: string[]) => (
  correctableChecks.some(name => row.scores[name] && Math.round(row.scores[name].value) === 0)
);

// Band misses can't be fixed surgically (a reading-level rewrite always trips the not-surgical
// guard), so those rows offer regeneration instead of a correction draft.
const hasBandMiss = (row: GenerationRow) => {
  const fit = row.scores['lr-reading-level-fit'];
  return Boolean(fit && Math.round(fit.value) === 0);
};

const chipClassForScore = (isBinary: boolean, value: number) => {
  if (isBinary) return value === 0 ? 'low' : 'ok';

  return JUDGE_CHIP_CLASSES[value] || 'mid';
};

const chipTextForScore = (isBinary: boolean, value: number) => {
  if (!isBinary) return String(value);

  return value === 0 ? '⚠' : '✓';
};

interface ScoreDetailsProps {
  colSpan: number;
  meta: GenerationsMeta;
  row: GenerationRow;
}

interface ScoreDetailGroupProps {
  hint?: string;
  isBinary: boolean;
  label: string;
  names: string[];
  scores: Record<string, GenerationScore>;
}

const ScoreDetailGroup = ({ hint, isBinary, label, names, scores }: ScoreDetailGroupProps) => {
  if (names.length === 0) return null;

  return (
    <>
      <div className="fail-group-label">{label}</div>
      {hint && <p className="note" style={{ margin: '0 0 6px' }}>{hint}</p>}
      {names.map((name) => {
        const score = scores[name];
        const value = Math.round(score.value);
        return (
          <div key={name} className="score-detail">
            <span className={`chip ${chipClassForScore(isBinary, value)}`}>
              {chipTextForScore(isBinary, value)}
            </span>
            <div>
              <strong style={{ fontSize: 12.5 }}>{scoreLabel(name)}</strong>
              <p>{score.comment || (isBinary ? 'Passed.' : 'No reasoning provided.')}</p>
            </div>
          </div>
        );
      })}
    </>
  );
};

// Full-width panel with every score's reasoning as readable text, grouped so deterministic
// checks and LLM judge scores are visually distinct — judge comments are paragraphs, not
// tooltip material.
const ScoreDetails = ({ colSpan, meta, row }: ScoreDetailsProps) => {
  const judges = meta.judgeNames.filter(name => row.scores[name]);
  const checks = [meta.firstAttemptName, ...meta.checkNames].filter(name => row.scores[name]);

  return (
    <tr className="detail-row">
      <td colSpan={colSpan}>
        {judges.length === 0 && checks.length === 0 && (
          <p className="note" style={{ margin: 0 }}>No scores yet — judges may still be evaluating.</p>
        )}
        <ScoreDetailGroup isBinary={false} label="LLM judge scores" names={judges} scores={row.scores} />
        <ScoreDetailGroup
          isBinary
          hint={'All checks describe the saved content; blocking violations never save, so flags here '
            + 'are warning-tier (⚠). First Attempt Pass ✗ records what an earlier attempt got wrong.'}
          label="Deterministic checks"
          names={checks}
          scores={row.scores}
        />
      </td>
    </tr>
  );
};

interface RowProps {
  expanded: boolean;
  meta: GenerationsMeta;
  regenerated: boolean;
  requested: boolean;
  row: GenerationRow;
  onDraft: (row: GenerationRow) => void;
  onRegenerate: (row: GenerationRow) => void;
  onToggle: (traceId: string) => void;
}

const GenerationTableRow = ({
  expanded, meta, regenerated, requested, row, onDraft, onRegenerate, onToggle,
}: RowProps) => {
  const firstAttempt = row.scores[meta.firstAttemptName];
  const colSpan = meta.judgeNames.length + 9;
  const canDraft = row.sectionReadingLevelId !== null
    && hasCorrectableWarning(row, meta.correctableChecks || []);
  const canRegenerate = hasBandMiss(row);

  return (
    <>
      <tr className={row.attention ? 'attention' : undefined}>
        <td style={{ paddingRight: 0, width: 28 }}>
          <button
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} score details for ${row.sectionTitle}`}
            className="expand-btn"
            type="button"
            onClick={() => onToggle(row.traceId)}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td>
          {row.sectionUrl ? (
            <a href={row.sectionUrl} rel="noopener noreferrer" target="_blank">{row.sectionTitle}</a>
          ) : row.sectionTitle}
          {(row.tags || []).length > 0 && (
            <div>
              {(row.tags || []).map(tag => (
                <span key={tag} className="trace-tag">{TAG_LABELS[tag] || tag}</span>
              ))}
            </div>
          )}
        </td>
        <td><code>{row.gradeBand}</code></td>
        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{row.generatedAt}</td>
        <td style={{ fontSize: 12 }}>{row.model}</td>
        {meta.judgeNames.map(name => <JudgeCell key={name} score={row.scores[name]} />)}
        <td style={{ textAlign: 'center' }}>
          {firstAttempt ? (
            <ScoreChip
              chipClass={Math.round(firstAttempt.value) === 0 ? 'fail' : 'ok'}
              text={Math.round(firstAttempt.value) === 0 ? '✗' : '✓'}
              title={firstAttempt.comment || 'Passed on the first attempt'}
            />
          ) : <span className="chip none">—</span>}
        </td>
        <ChecksCell checkNames={meta.checkNames} scores={row.scores} />
        <CorrectionCell
          canDraft={canDraft}
          canRegenerate={canRegenerate}
          correction={row.correction}
          correctionsUrl={meta.correctionsUrl}
          regenerated={regenerated}
          requested={requested}
          onDraft={() => onDraft(row)}
          onRegenerate={() => onRegenerate(row)}
        />
        <td style={{ textAlign: 'center' }}>
          <a
            href={`${meta.langfuseBase}/traces/${row.traceId}`}
            rel="noopener noreferrer"
            style={{ fontSize: 16 }}
            target="_blank"
            title={row.traceId}
          >
            ↗
          </a>
        </td>
      </tr>
      {expanded && <ScoreDetails colSpan={colSpan} meta={meta} row={row} />}
    </>
  );
};

interface LlmEvalGenerationsProps {
  initialData: GenerationsPayload;
  pollIntervalMs?: number;
  pollUrl: string;
}

// Live "All generations" table for the LLM eval dashboard. Judge scores arrive from Langfuse
// asynchronously after a generation, so the table refreshes itself instead of requiring page
// reloads. Renders the same markup/classes as the server template so the page's scoped styles
// apply unchanged.
const LlmEvalGenerations = ({ initialData, pollIntervalMs = POLL_INTERVAL_MS, pollUrl }: LlmEvalGenerationsProps) => {
  const [data, setData] = useState<GenerationsPayload>(initialData);
  const [page, setPage] = useState(1);
  const [regeneratedIds, setRegeneratedIds] = useState<string[]>([]);
  const [requestedIds, setRequestedIds] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const requestDraft = (row: GenerationRow) => {
    setupCsrfToken();
    Axios.post(data.meta.correctionsUrl, { section_reading_level_id: row.sectionReadingLevelId })
      .then(() => setRequestedIds(prev => [...prev, row.traceId]))
      .catch(() => { /* leave the button; the user can retry */ });
  };

  const requestRegenerate = (row: GenerationRow) => {
    setupCsrfToken();
    Axios.post(data.meta.regenerateUrl, { grade_band: row.gradeBand, section_id: row.sectionId })
      .then(() => setRegeneratedIds(prev => [...prev, row.traceId]))
      .catch(() => { /* leave the button; the user can retry */ });
  };
  // Rows needing attention start expanded so their reasoning is readable without interaction.
  const [expandedIds, setExpandedIds] = useState<string[]>(
    () => initialData.rows.filter(row => row.attention).map(row => row.traceId),
  );

  const toggleExpanded = (traceId: string) => {
    setExpandedIds(prev => (
      prev.includes(traceId) ? prev.filter(id => id !== traceId) : [...prev, traceId]
    ));
  };

  useEffect(() => {
    const poll = () => {
      if (document.hidden) return;

      Axios.get<GenerationsPayload>(pollUrl)
        .then((res) => {
          setData(res.data);
          setUpdatedAt(new Date());
        })
        .catch(() => { /* transient poll failures keep the last good data */ });
    };

    const timer = window.setInterval(poll, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [pollIntervalMs, pollUrl]);

  if (data.rows.length === 0) {
    return <div className="card empty-state">No generations found in this window. Adjust the date filter above.</div>;
  }

  // Clamp instead of resetting on poll updates so a shrinking window can't strand the pager.
  const totalPages = Math.max(1, Math.ceil(data.rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = data.rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

  return (
    <div>
      <div className="tablebox">
        <table cellPadding={0} cellSpacing={0} className="tabular_data" style={{ minWidth: 1000 }}>
          <thead>
            <tr>
              <th aria-label="Expand" style={{ width: 28 }} />
              <th style={{ minWidth: 200 }}>Section</th>
              <th>Band</th>
              <th>Generated</th>
              <th style={{ minWidth: 110 }}>Model</th>
              {data.meta.judgeNames.map(name => (
                <th key={name} style={{ minWidth: 78, textAlign: 'center' }}>{scoreLabel(name)}</th>
              ))}
              <th
                style={{ textAlign: 'center' }}
                title="First attempt passed the deterministic checks (✗ = corrected via retry)"
              >
                First Attempt
              </th>
              <th
                style={{ textAlign: 'center' }}
                title="Deterministic output checks on the persisted content — flags are warning-tier"
              >
                Checks
              </th>
              <th style={{ textAlign: 'center' }} title="Latest AI correction proposal for this reading level">
                Correction
              </th>
              <th style={{ textAlign: 'center' }}>Trace</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(row => (
              <GenerationTableRow
                key={row.traceId}
                expanded={expandedIds.includes(row.traceId)}
                meta={data.meta}
                regenerated={regeneratedIds.includes(row.traceId)}
                requested={requestedIds.includes(row.traceId)}
                row={row}
                onDraft={requestDraft}
                onRegenerate={requestRegenerate}
                onToggle={toggleExpanded}
              />
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ alignItems: 'center', display: 'flex', gap: 10, marginTop: 8 }}>
          <Button outlineStyle small disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            ← Prev
          </Button>
          <span className="note">
            {(currentPage - 1) * ROWS_PER_PAGE + 1}–{Math.min(currentPage * ROWS_PER_PAGE, data.rows.length)}
            {' of '}
            {data.rows.length}
          </span>
          <Button outlineStyle small disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
            Next →
          </Button>
        </div>
      )}
      {updatedAt && (
        <p className="note" style={{ marginTop: 6 }}>
          Scores auto-refresh every {Math.round(pollIntervalMs / 1000)}s —
          last updated {updatedAt.toLocaleTimeString()}.
        </p>
      )}
    </div>
  );
};

export default LlmEvalGenerations;
